import { useLayoutEffect, useRef, useState, type FocusEvent, type FormEvent } from 'react';
import TextInput from '../form/TextInput';
import TextArea from '../form/TextArea';
import NumberField from '../form/NumberField';
import { fieldWidthStyle, sizesControl, type BaseFieldProps } from '../form/fieldUtils';
import { useLabelPlacement, type LabelPlacement } from '../form/labelPlacement';
import './AppTextBox.css';

/*
 * The app's shared text field. It used to wrap DevExtreme's TextBox / TextArea /
 * NumberBox; it now renders the shared MUI fields in components/form (TextInput,
 * TextArea, NumberField) with the same props, so no caller had to change.
 *
 * What changed for callers:
 * - `label` follows the app-wide label placement (User Settings > Field labels):
 *   on the outline by default, or above / beside the field. It is the input's
 *   accessible name in every placement.
 * - onValueChanged fires only for the user's own changes (typing, clearing,
 *   stepping), never because the `value` prop changed. DevExtreme also raised it
 *   when the value was set in code, which made seeded forms look edited.
 * - A number box shows a numeric 0 as "0". DevExtreme's wrapper showed it empty.
 * - Multi-line `rows` now applies (DevExtreme's height was overridden to auto,
 *   which left every text area about two lines tall).
 */

interface BaseProps {
  /** Label text, placed by the label placement setting (on the outline by
   *  default) and tied to the input, so pass it only when this component owns
   *  the field's visible label. When the caller
   *  already renders one (a `<label className="form-label">`, a section
   *  header), use `ariaLabel` instead; two copies of the same text on one
   *  field is a duplicate accessible name, not a second label. */
  label?: string;
  /** Accessible name for the input when the visible label lives outside this
   *  component. Supplies aria-label without painting a second visible label.
   *  Ignored when `label` is set. */
  ariaLabel?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** Adds the required marker to the label and aria-required to the input. */
  required?: boolean;
  /** Where the label goes for this box only. Default: the app-wide setting. */
  labelPlacement?: LabelPlacement;
  /** The box's width (a number is px). Beside a label the label column is
   *  added outside it, so a px width is the box alone in every placement. */
  width?: number | string;
  error?: boolean;
  /** Shown under the field; in the error colour (and as the error message)
   *  when `error` is set. */
  helperText?: string;
  className?: string;
  maxLength?: number;
  placeholder?: string;
  /** Focus left the field. The event's target is the input, so its
   *  `value` is what the user typed even before a pending value change
   *  has re-rendered. */
  onBlur?: (e: FocusEvent<HTMLDivElement>) => void;
  /** Show a clear button while there is text. Off by default. Text only. */
  showClearButton?: boolean;
  /** Extra attributes for the underlying input, merged over the
   *  accessibility attributes derived from `error` / `helperText`.
   *  Pass an `id` here when the caller renders its own
   *  `<label htmlFor>` instead of using `label`; that is how
   *  SettingsInput keeps its external field label wired up.
   *  An `aria-label` here names the field like `ariaLabel`. */
  inputAttr?: Record<string, unknown>;
  /** Report the value only when the field is committed (focus leaves it or
   *  Enter is pressed in a single-line field) instead of on every keystroke.
   *
   *  Leave it off for ordinary form fields: a Save or Create button gated on
   *  the typed text has to see that text while the user is still in the box.
   *  Turn it on when `onValueChanged` does more than store the value: saves,
   *  fetches, or rewrites the number (clamping to a range, rejecting values)
   *  in a way that would fight the user halfway through typing it. */
  commitOnBlur?: boolean;
}

interface TextProps extends BaseProps {
  mode?: 'text' | 'tel' | 'email' | 'url' | 'password';
  value: string;
  onValueChanged: (value: string) => void;
  multiline?: false;
  type?: never;
}

interface NumberProps extends BaseProps {
  mode?: never;
  type: 'number';
  value: number | string;
  /** The typed number; an emptied box reports 0. */
  onValueChanged: (value: number) => void;
  multiline?: false;
  min?: number;
  max?: number;
  step?: number;
  /** Render -/+ step buttons inside the field. Off by default. */
  showSpinButtons?: boolean;
}

interface MultilineProps extends BaseProps {
  mode?: never;
  type?: never;
  multiline: true;
  value: string;
  onValueChanged: (value: string) => void;
  /** Visible lines. Default 3. */
  rows?: number;
}

type AppTextBoxProps = TextProps | NumberProps | MultilineProps;

/** DevExtreme-era inputAttr keys, as the shared fields expect them. The
 *  aria-label comes out (it is a name, passed as ariaLabel) because the
 *  shared fields set aria-label after spreading inputAttr. */
function splitInputAttr(inputAttr?: Record<string, unknown>) {
  if (!inputAttr) return { attr: undefined, name: undefined };
  const { 'aria-label': name, inputmode, ...rest } = inputAttr;
  const attr: Record<string, unknown> = { ...rest };
  if (inputmode != null && attr.inputMode == null) attr.inputMode = inputmode;
  return { attr, name: typeof name === 'string' ? name : undefined };
}

const toNumber = (v: number | string | null | undefined): number | null => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function AppTextBox(props: AppTextBoxProps) {
  const { label, ariaLabel, disabled, readOnly, required, labelPlacement, width, error, helperText, className, placeholder, onBlur, showClearButton, inputAttr, commitOnBlur, maxLength } = props;
  const placement = useLabelPlacement(labelPlacement);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Where the caret was when the user typed. A caller that rewrites the text
  // (upper-casing it, say) hands back a different value, React writes it into
  // the input, and the browser puts the caret at the end: typing in the middle
  // of a word would jump. Put it back when the text is the same length, which
  // is the upper-casing case.
  const pendingCaret = useRef<{ start: number; end: number; length: number } | null>(null);
  const rememberCaret = (e: FormEvent<HTMLDivElement>) => {
    const el = e.target;
    if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return;
    if (el.selectionStart == null || el.selectionEnd == null) return;
    pendingCaret.current = { start: el.selectionStart, end: el.selectionEnd, length: el.value.length };
    // The re-render it is for runs before this; anything later is unrelated.
    setTimeout(() => { pendingCaret.current = null; }, 0);
  };
  useLayoutEffect(() => {
    const caret = pendingCaret.current;
    if (!caret) return;
    pendingCaret.current = null;
    const el = document.activeElement;
    if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) || !wrapRef.current?.contains(el)) return;
    if (el.value.length === caret.length && (el.selectionStart !== caret.start || el.selectionEnd !== caret.end)) {
      el.setSelectionRange(caret.start, caret.end);
    }
  });

  // The number the user last left in the box, while they are in it: null for
  // emptied. Many callers store an empty box as 0 or null, or store a typed 0
  // as null ("v || null"). Showing the caller's echo of that would rewrite the
  // box under the user: a typed "0" would vanish before ".5" could follow, and
  // a cleared box would refill with "0". So while the caller's value is still
  // "nothing or zero" and so was the box's, show the box as the user left it.
  // Forgotten when focus leaves the field, after which the caller's value
  // shows as it is (0 as "0", null as empty).
  const [lastNumber, setLastNumber] = useState<{ raw: number | null } | null>(null);

  const { attr, name: attrName } = splitInputAttr(inputAttr);
  const shared: BaseFieldProps = {
    label: label || undefined,
    ariaLabel: label ? undefined : (ariaLabel || attrName),
    placeholder,
    disabled,
    readOnly,
    required,
    labelPlacement: placement,
    // With `error`, the helper text is the error message.
    error: error || undefined,
    errorText: error && helperText ? helperText : undefined,
    helperText: !error && helperText ? helperText : undefined,
    inputAttr: attr,
  };

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setLastNumber(null);
    onBlur?.(e);
  };

  const wrapProps = {
    ref: wrapRef,
    className: `app-textbox-wrap${error ? ' app-textbox-error' : ''}${sizesControl(width, placement, !!label) ? ' app-textbox-wrap--sized' : ''}${className ? ` ${className}` : ''}`,
    // The wrapper carries the width; beside a label a px width is the box's.
    style: width != null ? { ...fieldWidthStyle(width, placement, !!label), maxWidth: undefined } : undefined,
    onBlur: handleBlur,
  };

  if (props.multiline) {
    return (
      <div {...wrapProps} onInputCapture={rememberCaret}>
        <TextArea
          {...shared}
          value={props.value}
          onValueChanged={(v) => props.onValueChanged(v ?? '')}
          commitOnBlur={commitOnBlur}
          rows={props.rows}
          maxLength={maxLength}
        />
      </div>
    );
  }

  if (props.type === 'number') {
    const incoming = toNumber(props.value);
    const zeroish = (n: number | null | undefined) => n == null || n === 0;
    const shown = lastNumber && zeroish(lastNumber.raw) && zeroish(incoming) ? lastNumber.raw : incoming;
    return (
      <div {...wrapProps}>
        <NumberField
          {...shared}
          value={shown}
          emptyValue={null}
          onValueChanged={(n) => {
            setLastNumber((prev) => (prev?.raw === n ? prev : { raw: n }));
            props.onValueChanged(n ?? 0);
          }}
          commitOnBlur={commitOnBlur}
          min={props.min}
          max={props.max}
          step={props.step}
          showSpinButtons={props.showSpinButtons ?? false}
        />
      </div>
    );
  }

  return (
    <div {...wrapProps} onInputCapture={rememberCaret}>
      <TextInput
        {...shared}
        value={props.value}
        onValueChanged={(v) => props.onValueChanged(v ?? '')}
        commitOnBlur={commitOnBlur}
        mode={props.mode ?? 'text'}
        showClearButton={showClearButton}
        maxLength={maxLength}
      />
    </div>
  );
}
